<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, string, date } from 'yup'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  DCard,
  DBtn,
  DTextField,
  DMultiselect,
  DSelectFieldSearch,
  DModal,
  DLoading
} from 'front-library'
import {
  ReportMagistery,
  generateMagistery,
  getConcepts,
  getProcessCountry,
  getReportRequest
} from '@/app/core/services/reports'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos */
const itemsConcepts = ref<any[]>([])
const allConcepts = ref<any[]>([])
const allIdsConceptsSelected = ref<any[]>([])
const itemsProcesses = ref<string[]>([])
const itemsStatusPayroll = ref([
  { id: 'PRENOMINA', name: 'PRENOMINA' },
  { id: 'NOMINA', name: 'NOMINA' }
])
const searchStatus = ref<string>('')
const searchProcess = ref<string>('')
const urlFile = ref(null)
const refresh = ref()

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  concepts: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  startDate: date().required(t('form.valid.required')),
  endDate: date().when('startDate', (startDate: any, schema) => {
    if (startDate[0] !== undefined) {
      return schema
        .min(
          startDate,
          `${t('form.valid.startDateMin')} ${dayjs(startDate).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(t('form.valid.required'))
    }
    return schema.required(t('form.valid.required'))
  }),
  status: string().required(t('form.valid.required')),
  process: string().required(t('form.valid.required'))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  initialValues: {},
  validationSchema
})

/* Definicion campos del formulario */
const { value: concepts } = useField<string[]>(ref('concepts'))
const { value: startDate } = useField<string | undefined>('startDate')
const { value: endDate } = useField<string | undefined>('endDate')
const { value: status } = useField<string>('status')
const { value: process } = useField<string>('process')

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  const path = route.params.code as string
  const decodeCode = atob(path)
  const createReport: ReportMagistery = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateMagistery(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado del la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null
  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Listar conceptos */
const listConcepts = async () => {
  loading.value = true
  messageLoading.value = t('loading.concepts')
  itemsConcepts.value = []
  try {
    if (process.value?.length > 0) {
      if (concepts.value?.length > 0) concepts.value = []
      allConcepts.value = await getConcepts(
        "'" + storePayroll.payroll?.id + "'",
        'magisterio',
        '',
        "'" + process.value + "'"
      )
      itemsConcepts.value = [
        ...new Map(
          allConcepts.value.map((item: any) => [item['code'], item])
        ).values()
      ]
      if (itemsConcepts.value.length === 0) {
        notifications.addNotification({
          title: t('reports.notification.noConceptsFound'),
          text: t('reports.notification.noConcepts'),
          type: 'warning',
          time: 10000
        })
      }
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Filtrar codigos de conceptos */
const filterCodeConcepts = async (concepts: any) => {
  allIdsConceptsSelected.value = allConcepts.value
  allIdsConceptsSelected.value = allIdsConceptsSelected.value
    .filter(item => item.code === concepts)
    .map(_ => _.id)
}

/* Validar fecha */
const validateDate = () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    listConcepts()
    itemsProcesses.value = await getProcessCountry()
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.magisterium')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-8 gap-4">
                <div class="col-span-6 sm:col-span-4 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.process')"
                    v-model="process"
                    :options="filterOptions(itemsProcesses, searchProcess)"
                    :required="true"
                    :error="errors.process"
                    @filter="searchProcess = $event"
                    @change="listConcepts()" />
                </div>
                <div class="col-span-6 sm:col-span-4 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.concepts')"
                    v-model="concepts"
                    :headerTitle="t('reports.fields.concepts')"
                    :required="true"
                    :options="itemsConcepts"
                    :error="errors.concepts"
                    @change="filterCodeConcepts(concepts)" />
                </div>
                <div class="col-span-6 sm:col-span-4 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.startDate')"
                    :required="true"
                    v-model="startDate"
                    :error="errors.startDate"
                    @change="validateDate" />
                </div>
                <div class="col-span-6 sm:col-span-4 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.endDate')"
                    :required="true"
                    v-model="endDate"
                    :error="errors.endDate"
                    @change="validateDate" />
                </div>
                <div class="col-span-6 sm:col-span-4 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.state')"
                    v-model="status"
                    :options="filterOptions(itemsStatusPayroll, searchStatus)"
                    :required="true"
                    :error="errors.status"
                    @filter="searchStatus = $event" />
                </div>
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="t('form.button.file')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
